import { useEffect, useMemo, useState } from "react";
import { ChartTooltip, TimeChart, formatTime } from "@mops/charts-v2";
import type { Series } from "@mops/charts-v2";
import "./Usage.css";
import { Button } from "./Button";
import { Alert, Loading } from "./Card";
import { Input } from "./Field";
import { STATUS, type StatusKey } from "./Status";
import { tx } from "../i18n";

/* ══ 사용이력 카드 — 채택 시안 A안(Figma 10-3, 상태 판 12-2) ══
   대시보드 맨 위에 가득 폭으로 선다. 기간은 카드 머리의 칩 넷, 특정일은 칩 옆 날짜 입력.

   **화면은 칸을 계산하지 않는다** — `GET /usage/history` 가 칸 시작(slots)과 상태별 수(series)를
   다 채워 준다(crates/api/src/usage.rs). 화면은 그것을 공통 차트 모듈(@mops/charts-v2)에 그대로 넘긴다.
   손 SVG 로 막대를 그리지 않는다(공통 COMMON.md 「차트」).

   **0 과 「모름」을 가른다** — 서버가 `unknown` 을 주면 칸을 그리지 않고 그 갈래의 문장과 다음 할 일을
   보인다. 0 칸으로 그리면 「전이가 없었다」로 읽힌다(docs/decisions/usage-history.md 「빈 칸과 모름」). */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

/** 칸 폭 — 서버의 `SLOT_MINUTES` 와 같다. 응답의 `slotMinutes` 를 쓰고, 못 읽으면 이 값이다. */
const SLOT_MS = 30 * 60 * 1000;
const TZ = "Asia/Seoul";

export type UsageRange = "1d" | "day" | "7d" | "30d";
export const RANGES: UsageRange[] = ["1d", "day", "7d", "30d"];

export type UsageData = {
  range: string;
  slotMinutes?: number;
  slots?: number[];
  series?: { status: string; counts: number[] }[];
  truncated?: boolean;
  behind: number | null;
  unknown: null | { kind: string; detail: string | null; missing: string[] };
};

/** 쌓는 순서(아래 → 위)와 계열색 — docs/decisions/usage-history.md 「상태 색」 표. 색은 토큰 이름만 쓴다.
 *  상태색(--success 등)이 아니라 계열색인 이유: 상태색은 넷뿐이라 일곱을 못 가른다. */
export const STACK: { key: StatusKey; color: string }[] = [
  { key: "todo", color: "--chart-9" },
  { key: "ready", color: "--chart-6" },
  { key: "in-progress", color: "--chart-1" },
  { key: "review", color: "--chart-4" },
  { key: "done", color: "--chart-2" },
  { key: "blocked", color: "--chart-5" },
  { key: "cancelled", color: "--chart-3" },
];

const label = (k: StatusKey) => `${STATUS[k].glyph} ${STATUS[k].label}`;

/** 서버가 준 갈래를 문장으로 — 판정은 서버가 했다, 화면은 옮길 뿐이다. */
function unknownText(kind: string): string {
  if (kind === "unreachable") return tx("usage.unknown.unreachable");
  if (kind === "unauthorized") return tx("usage.unknown.unauthorized");
  if (kind === "no-spec") return tx("usage.unknown.noSpec");
  return tx("usage.unknown.other", { kind });
}

/** 그리기만 하는 판 — 패턴 페이지가 상태별로 같은 것을 부른다. */
export function UsageView({
  data,
  loading,
  error,
  range,
  day,
  today,
  onRange,
  onDay,
  onRetry,
  isAdmin = true,
}: {
  data: UsageData | null;
  loading?: boolean;
  error?: string | null;
  range: UsageRange;
  day: string;
  today: string;
  onRange: (r: UsageRange) => void;
  onDay: (d: string) => void;
  onRetry?: () => void;
  isAdmin?: boolean;
}) {
  const [off, setOff] = useState<Set<StatusKey>>(new Set());
  const [hover, setHover] = useState<number | null>(null);

  const slotMs = (data?.slotMinutes ?? 30) * 60 * 1000 || SLOT_MS;
  const slots = data?.slots ?? [];
  const counts = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const s of data?.series ?? []) m.set(s.status, s.counts);
    return m;
  }, [data]);

  const shown = STACK.filter((s) => !off.has(s.key));
  const series: Series[] = shown.map((s) => ({ key: s.key, label: label(s.key), color: s.color, stack: "usage" }));
  const chartData = useMemo(() => {
    const d: { x: number[] } & Record<string, number[]> = { x: slots };
    for (const s of STACK) d[s.key] = counts.get(s.key) ?? slots.map(() => 0);
    return d;
  }, [slots, counts]);
  const win = slots.length ? { from: slots[0], to: slots[slots.length - 1] + slotMs } : undefined;
  const totals = STACK.map((s) => ({ key: s.key, n: (counts.get(s.key) ?? []).reduce((a, b) => a + b, 0) }));
  const total = totals.reduce((a, t) => a + t.n, 0);

  function toggle(k: StatusKey) {
    setOff((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  const tipRows =
    hover == null
      ? []
      : shown.map((s) => ({ key: s.key, label: label(s.key), color: s.color, value: String(counts.get(s.key)?.[hover] ?? 0) }));
  const tipTotal = tipRows.reduce((a, r) => a + Number(r.value), 0);

  let body;
  if (error) {
    body = <Alert tone="error">{tx("usage.loadFailed", { err: error })}</Alert>;
  } else if (loading || !data) {
    // 읽는 동안 축·범례를 그리지 않는다 — 빈 판이 「0 건」으로 읽히지 않게.
    body = <Loading text={tx("usage.loading")} />;
  } else if (data.unknown?.kind === "not-configured") {
    body = (
      <Alert tone="warning">
        {tx("usage.notConfigured")}{" "}
        {isAdmin ? (
          <a className="b-btn b-btn-secondary b-btn-sm" href="#/conn">
            {tx("usage.openConn")}
          </a>
        ) : (
          tx("usage.askAdmin")
        )}
      </Alert>
    );
  } else if (data.unknown) {
    body = (
      <Alert tone="error">
        {unknownText(data.unknown.kind)}
        {data.unknown.detail ? ` [${data.unknown.detail}]` : ""}{" "}
        {onRetry ? (
          <Button size="sm" onClick={onRetry}>
            {tx("common.action.retry")}
          </Button>
        ) : null}
      </Alert>
    );
  } else {
    body = (
      <>
        <div className="usage__legend" role="group" aria-label={tx("usage.legendLabel")}>
          {STACK.map((s) => {
            const on = !off.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                className={`b-chip usage__key${on ? "" : " is-off"}`}
                aria-pressed={on}
                title={tx("usage.toggleTip")}
                onClick={() => toggle(s.key)}
              >
                <i className="usage__swatch" style={{ ["--swatch" as string]: `var(${s.color})` }} aria-hidden="true" />
                {label(s.key)}
              </button>
            );
          })}
        </div>
        <div className="usage__plot">
          <ChartTooltip
            ratio={hover == null || !win ? null : (slots[hover] - win.from) / (win.to - win.from)}
            offset={48}
            time={hover == null ? "" : `${formatTime(slots[hover], TZ)} – ${formatTime(slots[hover] + slotMs, TZ)} · ${tx("usage.totalN", { n: tipTotal })}`}
            rows={tipRows}
          />
          <TimeChart
            data={chartData}
            series={series}
            bucketMs={slotMs}
            window={win}
            height={200}
            yAxisSize={48}
            // 건수는 정수다 — 소수 눈금을 지운다. 전부 0 이면 축이 0~1 로 쪼개지지 않게 높이를 잡는다.
            y={{ min: 0, max: total === 0 ? 4 : undefined, format: (v) => (Number.isInteger(v) ? String(v) : "") }}
            timeZone={TZ}
            onHover={(i) => setHover(i)}
            ariaLabel={tx("usage.chartLabel")}
          />
        </div>
        {total === 0 ? (
          <p className="usage__note">
            <b>{tx("usage.empty")}</b> {tx("usage.emptyDesc")}
          </p>
        ) : null}
        {data.behind ? <Alert tone="info">{tx("usage.behind", { n: data.behind })}</Alert> : null}
        {data.truncated ? <Alert tone="warning">{tx("usage.truncated")}</Alert> : null}
        <p className="usage__totals">
          <span className="b-eyebrow">{tx("usage.total")}</span>
          {totals.map((t) => (
            <span key={t.key} className={`tnum${off.has(t.key) ? " usage__muted" : ""}`}>
              {label(t.key)} {t.n}
            </span>
          ))}
        </p>
      </>
    );
  }

  return (
    <section className="b-card usage" aria-label={tx("usage.title")}>
      <header className="usage__head">
        <div className="usage__titles">
          <h2 className="b-card-title">{tx("usage.title")}</h2>
          <p className="caption">{tx("usage.sub")}</p>
        </div>
        <div className="usage__ranges" role="group" aria-label={tx("usage.rangeLabel")}>
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              className={`b-chip${range === r ? " is-on" : ""}`}
              aria-pressed={range === r}
              onClick={() => onRange(r)}
            >
              {tx(`usage.range.${r}`)}
            </button>
          ))}
          {range === "day" ? (
            <Input
              size="md"
              className="usage__day"
              type="date"
              value={day}
              max={today}
              aria-label={tx("usage.dayLabel")}
              onChange={(e) => onDay(e.target.value)}
            />
          ) : null}
        </div>
      </header>
      {body}
    </section>
  );
}

/** 오늘(한국 시각) — 특정일의 기본값이자 상한. 서버도 미래 날짜를 400 으로 거절한다. */
function todayKst(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

/** 대시보드에 서는 카드 — 기간을 바꾸면 그 기간을 다시 읽는다. 타이머로 다시 읽지 않는다(새로고침 · 기간 바꾸기). */
export function UsageCard({ isAdmin }: { isAdmin: boolean }) {
  const today = todayKst();
  const [range, setRange] = useState<UsageRange>("1d");
  const [day, setDay] = useState(today);
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const q = range === "day" ? `day=${encodeURIComponent(day)}` : `range=${range}`;
    fetch(`${API}/usage/history?${q}`)
      .then(async (r) => {
        const v = await r.json().catch(() => null);
        // 400 은 서버가 무엇이 틀렸는지 말한다 — 그 문장을 그대로 보인다.
        if (!r.ok) throw new Error(v?.message ?? `HTTP ${r.status}`);
        return v as UsageData;
      })
      .then((v) => alive && setData(v))
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range, day, nonce]);

  return (
    <UsageView
      data={data}
      loading={loading}
      error={error}
      range={range}
      day={day}
      today={today}
      onRange={setRange}
      onDay={(d) => d && setDay(d)}
      onRetry={() => setNonce((n) => n + 1)}
      isAdmin={isAdmin}
    />
  );
}
